import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface CausticsEffectProps {
  intensity?: number;
  areaSize?: [number, number];
  color?: string;
  speed?: number;
}

export function CausticsEffect({
  intensity = 0.5,
  areaSize = [20, 20],
  color = '#4fc3f7',
  speed = 1,
}: CausticsEffectProps) {
  const lightRef1 = useRef<THREE.PointLight>(null);
  const lightRef2 = useRef<THREE.PointLight>(null);
  const lightRef3 = useRef<THREE.PointLight>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  // Create caustic pattern texture
  const causticsGeometry = useMemo(() => {
    const geometry = new THREE.PlaneGeometry(areaSize[0], areaSize[1], 32, 32);
    return geometry;
  }, [areaSize]);

  useFrame((state) => {
    const time = state.clock.elapsedTime * speed;

    // Animate point lights in wave patterns
    if (lightRef1.current) {
      lightRef1.current.position.x = areaSize[0] / 2 + Math.sin(time * 0.7) * 5;
      lightRef1.current.position.z = areaSize[1] / 2 + Math.cos(time * 0.5) * 5;
      lightRef1.current.intensity = (0.5 + Math.sin(time * 1.5) * 0.3) * intensity * 8;
    }

    if (lightRef2.current) {
      lightRef2.current.position.x = areaSize[0] / 2 + Math.cos(time * 0.8) * 4;
      lightRef2.current.position.z = areaSize[1] / 2 + Math.sin(time * 0.6) * 4;
      lightRef2.current.intensity = (0.5 + Math.cos(time * 1.2) * 0.3) * intensity * 6;
    }

    if (lightRef3.current) {
      lightRef3.current.position.x = areaSize[0] / 2 + Math.sin(time * 0.9 + 2) * 6;
      lightRef3.current.position.z = areaSize[1] / 2 + Math.cos(time * 0.7 + 1) * 6;
      lightRef3.current.intensity = (0.4 + Math.sin(time * 1.8) * 0.2) * intensity * 5;
    }

    // Animate caustics mesh
    if (meshRef.current) {
      const positions = meshRef.current.geometry.attributes.position;
      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const z = positions.getZ(i);
        const wave = Math.sin(x * 0.5 + time) * Math.cos(z * 0.5 + time * 0.7) * 0.1;
        positions.setY(i, wave);
      }
      positions.needsUpdate = true;
    }
  });

  return (
    <group>
      {/* Animated point lights creating caustic effect */}
      <pointLight
        ref={lightRef1}
        position={[areaSize[0] / 2, 3, areaSize[1] / 2]}
        color={color}
        intensity={intensity * 8}
        distance={15}
        decay={2}
      />
      <pointLight
        ref={lightRef2}
        position={[areaSize[0] / 4, 2.5, areaSize[1] / 4]}
        color="#80deea"
        intensity={intensity * 6}
        distance={12}
        decay={2}
      />
      <pointLight
        ref={lightRef3}
        position={[areaSize[0] * 3/4, 2, areaSize[1] * 3/4]}
        color="#26c6da"
        intensity={intensity * 5}
        distance={10}
        decay={2}
      />

      {/* Caustics pattern on floor */}
      <mesh
        ref={meshRef}
        geometry={causticsGeometry}
        position={[areaSize[0] / 2, 0.05, areaSize[1] / 2]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.15 * intensity}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Light rays from surface */}
      {Array.from({ length: 8 }).map((_, i) => (
        <LightRay
          key={i}
          position={[
            (i % 4) * (areaSize[0] / 3) + areaSize[0] / 6,
            0,
            Math.floor(i / 4) * (areaSize[1] / 2) + areaSize[1] / 4,
          ]}
          color={color}
          intensity={intensity}
          index={i}
        />
      ))}
    </group>
  );
}

interface LightRayProps {
  position: [number, number, number];
  color: string;
  intensity: number;
  index: number;
}

function LightRay({ position, color, intensity, index }: LightRayProps) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (!meshRef.current) return;
    const time = state.clock.elapsedTime;

    // Animate opacity and scale
    const opacity = (0.1 + Math.sin(time * 0.8 + index) * 0.05) * intensity;
    const material = meshRef.current.material as THREE.MeshBasicMaterial;
    material.opacity = opacity;

    // Gentle sway
    meshRef.current.rotation.x = -Math.PI / 2 + Math.sin(time * 0.3 + index) * 0.1;
    meshRef.current.rotation.z = Math.cos(time * 0.4 + index * 0.5) * 0.05;
  });

  return (
    <mesh ref={meshRef} position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <coneGeometry args={[0.5, 8, 8, 1, true]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.1 * intensity}
        side={THREE.DoubleSide}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}
